// One-shot, bottom-left advisory card that nudges underpowered users toward
// Pause → Settings → Graphics. Fires once, ever, when presented frames
// average below 30 FPS for 4 consecutive 1-second buckets after a 3-second
// warm-up. The "shown" state is persisted in localStorage so the card never
// re-appears for the same browser, even across maps and sessions.
//
// Style mirrors TutorialHintBanner's .thb-card: Chewy font, white rounded
// card with soft shadow, 280 ms opacity fade. FPS sampling pattern mirrors
// Hud.startFpsLoop.

import { t } from '../i18n';

const STORAGE_KEY     = 'games:lowFpsHintShown';
const STYLE_ID        = 'lfa-style';
const FADE_MS         = 280;
const HOLD_MS         = 7000;
const WARMUP_MS       = 3000;
const SUSTAIN_BUCKETS = 4;
const FPS_THRESHOLD   = 30;

const STYLE_BLOCK = `
@import url('https://fonts.googleapis.com/css2?family=Chewy&display=swap');

#lfa-root {
  position: fixed;
  bottom: calc(16px + env(safe-area-inset-bottom, 0px));
  left:   calc(16px + env(safe-area-inset-left, 0px));
  z-index: 12;
  pointer-events: none;
  font-family: 'Chewy', ui-sans-serif, system-ui, sans-serif;
}
.lfa-card {
  background: rgba(248, 250, 253, 0.96);
  color: #4a5568;
  border-radius: 18px;
  padding: 12px 22px;
  box-shadow: 0 6px 22px rgba(0, 0, 0, 0.18), 0 1px 0 rgba(255, 255, 255, 0.6) inset;
  font-size: 18px;
  letter-spacing: 0.02em;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  /* Matches FADE_MS. */
  transition: opacity 280ms ease;
  will-change: opacity;
  opacity: 0;
}
.lfa-row-sub { font-size: 16px; color: #6b7280; }
@media (max-width: 540px) {
  .lfa-card { font-size: 15px; padding: 10px 16px; }
  .lfa-row-sub { font-size: 13px; }
}
`;

export class LowFpsAdvisor {
  private disposed = false;
  private fired = false;

  private startMs = 0;
  private bucketStartMs = 0;
  private rafTimes: number[] = [];
  private subThirtyStreak = 0;
  private rafHandle = 0;

  private root: HTMLElement | null = null;
  private card: HTMLElement | null = null;
  private fadeOutTimer: number | null = null;
  private removeTimer: number | null = null;

  constructor() {
    let alreadyShown = false;
    try { alreadyShown = localStorage.getItem(STORAGE_KEY) === '1'; } catch { /* ignore */ }
    if (alreadyShown) {
      // Full no-op path — no DOM, no rAF.
      this.disposed = true;
      return;
    }
    if (!document.getElementById(STYLE_ID)) {
      const style = document.createElement('style');
      style.id = STYLE_ID;
      style.textContent = STYLE_BLOCK;
      document.head.appendChild(style);
    }
    this.startMs = performance.now();
    this.bucketStartMs = this.startMs;
    this.rafHandle = requestAnimationFrame(this.tick);
  }

  private tick = (now: number): void => {
    if (this.disposed) return;

    this.rafTimes.push(now);
    const cutoff = now - 1000;
    while (this.rafTimes.length > 0 && this.rafTimes[0]! < cutoff) {
      this.rafTimes.shift();
    }

    if (now - this.startMs < WARMUP_MS) {
      this.bucketStartMs = now;
      this.rafHandle = requestAnimationFrame(this.tick);
      return;
    }

    if (now - this.bucketStartMs >= 1000) {
      let fps = 0;
      if (this.rafTimes.length >= 2) {
        const span = this.rafTimes[this.rafTimes.length - 1]! - this.rafTimes[0]!;
        if (span > 0) fps = (this.rafTimes.length - 1) * 1000 / span;
      }
      if (fps > 0 && fps < FPS_THRESHOLD) this.subThirtyStreak += 1;
      else this.subThirtyStreak = 0;
      this.bucketStartMs = now;

      if (!this.fired && this.subThirtyStreak >= SUSTAIN_BUCKETS) {
        this.fired = true;
        this.show();
        return;
      }
    }

    this.rafHandle = requestAnimationFrame(this.tick);
  };

  private show(): void {
    // Persist immediately on fire so navigating away mid-display still
    // suppresses future shows.
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch { /* quota / private mode — ignore */ }

    // Stop measuring; we're done.
    if (this.rafHandle) {
      cancelAnimationFrame(this.rafHandle);
      this.rafHandle = 0;
    }

    const root = document.createElement('div');
    root.id = 'lfa-root';
    const card = document.createElement('div');
    card.className = 'lfa-card';
    const titleRow = document.createElement('div');
    titleRow.textContent = t('hud.lowFps.title');
    const bodyRow = document.createElement('div');
    bodyRow.className = 'lfa-row-sub';
    bodyRow.textContent = t('hud.lowFps.body');
    card.appendChild(titleRow);
    card.appendChild(bodyRow);
    root.appendChild(card);
    document.body.appendChild(root);
    this.root = root;
    this.card = card;

    // Kick the CSS opacity transition on the next rAF so it animates from
    // 0 → 1 rather than skipping straight to the final value.
    requestAnimationFrame(() => {
      if (this.card) this.card.style.opacity = '1';
    });

    this.fadeOutTimer = window.setTimeout(() => {
      if (this.card) this.card.style.opacity = '0';
    }, FADE_MS + HOLD_MS);

    this.removeTimer = window.setTimeout(() => {
      if (this.root) {
        this.root.remove();
        this.root = null;
        this.card = null;
      }
    }, FADE_MS + HOLD_MS + FADE_MS);
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    if (this.rafHandle) {
      cancelAnimationFrame(this.rafHandle);
      this.rafHandle = 0;
    }
    if (this.fadeOutTimer !== null) {
      clearTimeout(this.fadeOutTimer);
      this.fadeOutTimer = null;
    }
    if (this.removeTimer !== null) {
      clearTimeout(this.removeTimer);
      this.removeTimer = null;
    }
    if (this.root) {
      this.root.remove();
      this.root = null;
      this.card = null;
    }
  }
}
